import { useEffect, useRef, useState } from 'react';
import { IdentityNetworkError, IdentityRequestError } from '@/identity/client';
import { useStrings } from '@/i18n/Localization';
import { localeTag } from '@/i18n/strings';
import { accountFromVerify, useSession } from '@/transport/SessionProvider';
import styles from '@/screens/SignInScreen.module.css';

/**
 * A short list of dial codes, plus free entry.
 *
 * It is short on purpose. A complete list is a data file that has to be
 * maintained and localized, and the free-entry option below already accepts any
 * E.164 number, so the list is a convenience rather than a constraint. Country
 * names are English labels here because the interface language is English;
 * translating them belongs with the resource map when a second language lands.
 */
const DIAL_CODES: readonly { code: string; name: string }[] = [
  { code: '+1', name: 'United States and Canada' },
  { code: '+44', name: 'United Kingdom' },
  { code: '+49', name: 'Germany' },
  { code: '+33', name: 'France' },
  { code: '+34', name: 'Spain' },
  { code: '+39', name: 'Italy' },
  { code: '+31', name: 'Netherlands' },
  { code: '+46', name: 'Sweden' },
  { code: '+7', name: 'Russia and Kazakhstan' },
  { code: '+90', name: 'Türkiye' },
  { code: '+98', name: 'Iran' },
  { code: '+971', name: 'United Arab Emirates' },
  { code: '+966', name: 'Saudi Arabia' },
  { code: '+20', name: 'Egypt' },
  { code: '+91', name: 'India' },
  { code: '+86', name: 'China' },
  { code: '+81', name: 'Japan' },
  { code: '+82', name: 'South Korea' },
  { code: '+61', name: 'Australia' },
  { code: '+55', name: 'Brazil' },
];

type Stage = 'phone' | 'code';

/**
 * Sign in: country and phone, send code, countdown, code entry.
 *
 * Errors are shown as the server phrased them, and the server phrases them so
 * that they never reveal whether an account exists — Section 12 makes the
 * challenge response identical either way. Nothing here tries to be more
 * helpful than that, because "no account with that number" would undo the whole
 * property.
 */
export function SignInScreen() {
  const { t } = useStrings();
  const { identity, installationId, signIn } = useSession();

  const [stage, setStage] = useState<Stage>('phone');
  const [dialCode, setDialCode] = useState('+1');
  const [nationalNumber, setNationalNumber] = useState('');
  const [code, setCode] = useState('');
  const [challengeId, setChallengeId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendSeconds, setResendSeconds] = useState(0);

  const codeInputRef = useRef<HTMLInputElement | null>(null);

  const phoneE164 = `${dialCode}${nationalNumber.replace(/[^0-9]/g, '')}`;

  // The countdown is a plain interval over a second counter rather than a
  // wall-clock deadline, because it only has to look right; the server enforces
  // the actual limit and answers a premature resend with RATE_LIMITED.
  useEffect(() => {
    if (resendSeconds <= 0) {
      return;
    }
    const timer = setInterval(() => {
      setResendSeconds((value) => Math.max(0, value - 1));
    }, 1000);
    return () => {
      clearInterval(timer);
    };
  }, [resendSeconds]);

  useEffect(() => {
    if (stage === 'code') {
      codeInputRef.current?.focus();
    }
  }, [stage]);

  const describe = (caught: unknown): string => {
    if (caught instanceof IdentityRequestError) {
      if (caught.detail.code === 'RATE_LIMITED' && caught.detail.retryAfterMs !== null) {
        const seconds = Math.ceil(caught.detail.retryAfterMs / 1000);
        setResendSeconds(seconds);
        return t('signin.errorRateLimited', { seconds });
      }
      return caught.detail.message || t('signin.errorGeneric');
    }
    if (caught instanceof IdentityNetworkError) {
      return t('signin.errorNetwork');
    }
    return t('signin.errorGeneric');
  };

  const sendCode = async () => {
    if (nationalNumber.replace(/[^0-9]/g, '').length === 0) {
      setError(t('signin.errorPhone'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await identity.createChallenge(phoneE164, localeTag);
      setChallengeId(result.challengeId);
      setResendSeconds(Math.ceil(result.resendAfterMs / 1000));
      setStage('code');
    } catch (caught) {
      setError(describe(caught));
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    if (code.trim().length === 0) {
      setError(t('signin.errorCode'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await identity.verify({
        challengeId,
        code: code.trim(),
        // A short label for the session list in Settings. It is deliberately
        // not derived from the user agent: that string is long, mostly noise,
        // and a fingerprinting surface the client has no reason to volunteer.
        deviceName: 'Web browser',
        installationId,
      });
      await signIn(
        accountFromVerify({
          accountId: result.accountId,
          displayName: result.displayName,
          username: result.username,
          deviceId: result.deviceId,
          sessionId: result.sessionId,
          installationId,
          phoneE164,
        }),
        result.accessToken,
      );
    } catch (caught) {
      setError(describe(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className={styles.screen}>
      <div className={styles.card}>
        <h1 className={styles.title}>{t('signin.title')}</h1>

        {stage === 'phone' ? (
          <>
            <p className={styles.subtitle}>{t('signin.subtitle')}</p>

            <label className={styles.label} htmlFor="signin-country">
              {t('signin.country')}
            </label>
            <select
              id="signin-country"
              className={styles.select}
              value={dialCode}
              onChange={(event) => {
                setDialCode(event.target.value);
              }}
            >
              {DIAL_CODES.map((entry) => (
                <option key={entry.code} value={entry.code}>
                  {entry.name} ({entry.code})
                </option>
              ))}
            </select>

            <label className={styles.label} htmlFor="signin-phone">
              {t('signin.phone')}
            </label>
            <input
              id="signin-phone"
              className={styles.input}
              type="tel"
              inputMode="tel"
              autoComplete="tel-national"
              // The number is always LTR regardless of the interface direction:
              // a phone number reads left to right in every locale.
              dir="ltr"
              value={nationalNumber}
              placeholder={t('signin.phonePlaceholder')}
              onChange={(event) => {
                setNationalNumber(event.target.value);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  void sendCode();
                }
              }}
            />

            <button
              type="button"
              className={styles.primary}
              disabled={busy}
              onClick={() => {
                void sendCode();
              }}
            >
              {busy ? t('signin.sendingCode') : t('signin.sendCode')}
            </button>
          </>
        ) : (
          <>
            <h2 className={styles.subtitle}>{t('signin.codeTitle')}</h2>
            <p className={styles.subtitle} dir="ltr">
              {t('signin.codeSubtitle', { phone: phoneE164 })}
            </p>

            <label className={styles.label} htmlFor="signin-code">
              {t('signin.code')}
            </label>
            <input
              id="signin-code"
              ref={codeInputRef}
              className={styles.input}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              dir="ltr"
              value={code}
              placeholder={t('signin.codePlaceholder')}
              onChange={(event) => {
                setCode(event.target.value);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  void verify();
                }
              }}
            />

            <button
              type="button"
              className={styles.primary}
              disabled={busy}
              onClick={() => {
                void verify();
              }}
            >
              {busy ? t('signin.verifying') : t('signin.verify')}
            </button>

            <div className={styles.secondaryRow}>
              {resendSeconds > 0 ? (
                <span className={styles.countdown} role="status" aria-live="polite">
                  {t('signin.resendIn', { seconds: resendSeconds })}
                </span>
              ) : (
                <button
                  type="button"
                  className={styles.secondary}
                  disabled={busy}
                  onClick={() => {
                    void sendCode();
                  }}
                >
                  {t('signin.resend')}
                </button>
              )}
              <button
                type="button"
                className={styles.secondary}
                onClick={() => {
                  setStage('phone');
                  setCode('');
                  setError(null);
                }}
              >
                {t('signin.changeNumber')}
              </button>
            </div>
          </>
        )}

        {error !== null ? (
          <p className={styles.error} role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </main>
  );
}
